import React from "react";

interface SummaryCardProps {
  summary: string;
  rowCount: number | null;
  totalCount?: number | null;
}

/** Render a segment of text that may contain **bold** markers. */
function InlineText({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i} className="font-semibold text-gray-900">
            {part.slice(2, -2)}
          </strong>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

/** Split on blank lines into paragraphs; handle **bold** within each. */
function MarkdownSummary({ text }: { text: string }) {
  const paragraphs = text.split(/\n{2,}/);
  return (
    <div className="space-y-2">
      {paragraphs.map((para, i) => {
        const trimmed = para.trim();
        if (!trimmed) return null;
        // Single-newline inside a paragraph → line break
        const lines = trimmed.split("\n");
        return (
          <p key={i} className="text-sm text-gray-700 leading-7">
            {lines.map((line, j) => (
              <React.Fragment key={j}>
                <InlineText text={line} />
                {j < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

const SummaryCard: React.FC<SummaryCardProps> = ({ summary, rowCount, totalCount }) => {
  const isTruncated = totalCount != null && rowCount != null && totalCount > rowCount;
  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
      <div className="border-l-4 border-blue-500 px-5 py-4 space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            AI Analysis
          </span>
          {rowCount !== null && (
            <span
              title={isTruncated ? `Query matched ${totalCount!.toLocaleString()} records; showing top ${rowCount.toLocaleString()}. Download CSV for the full set.` : undefined}
              className={`flex items-center gap-1 text-xs rounded-full px-2.5 py-0.5 border
                ${isTruncated
                  ? "bg-amber-50 border-amber-200 text-amber-700 cursor-help"
                  : "bg-gray-50 border-gray-200 text-gray-400"
                }`}
            >
              {isTruncated && (
                <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              )}
              {isTruncated
                ? `${rowCount.toLocaleString()} of ${totalCount!.toLocaleString()} records`
                : `${rowCount.toLocaleString()} record${rowCount !== 1 ? "s" : ""}`
              }
            </span>
          )}
        </div>
        {isTruncated && (
          <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-1.5">
            Showing top {rowCount!.toLocaleString()} rows of {totalCount!.toLocaleString()} total matches.
            Download the CSV report for the full dataset.
          </p>
        )}
        <MarkdownSummary text={summary} />
      </div>
    </div>
  );
};

export default SummaryCard;
